import { useEffect, useMemo, useRef, useState } from "react";
import { errorCodeOf, readAllSessionStarts, type SessionStart } from "@heraion/shared";

import { summarizeSessions, type SessionsRecap } from "./progressMath";
import { useRetryOnReconnect } from "../useRetryOnReconnect";
import type { ProgressApiFetch } from "./types";

// Session summary (total / 7d / 30d / last + weekly frequency + PG-58 streak)
// from every /api/sessions page, no cache: web-rnw's Progress and the coach
// detail (CO-29, through the athlete-scoped fetch) read the same numbers.
// PG-38: walks every page, never one `?limit=200` page taken as the history.
// A new apiFetch is another reader (the coach's next athlete): what the last
// one loaded, or failed to, is dropped before the new load.
export function useSessionsSummary(
  apiFetch: ProgressApiFetch,
  weekStartDay: number | undefined,
  // Optional host defer (same shape as useChartWorkouts): the coach view passes
  // it so the paged read starts after first paint. Omitted = starts at mount.
  schedule?: (run: () => void) => () => void,
): SessionsRecap & { error: string | null } {
  const scheduleRef = useRef(schedule);
  scheduleRef.current = schedule;
  const [sessions, setSessions] = useState<SessionStart[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // #397: a read that failed offline runs again when the network is back.
  const retry = useRetryOnReconnect(error !== null);
  useEffect(() => {
    let cancelled = false;
    setSessions(null);
    setError(null);
    const run = () => {
      if (cancelled) return;
      void (async () => {
        try {
          const items = await readAllSessionStarts(apiFetch);
          if (cancelled) return;
          setSessions(items);
          setError(null);
        } catch (e) {
          if (!cancelled)
            setError(errorCodeOf(e));
        }
      })();
    };
    const cancelSchedule = scheduleRef.current?.(run);
    if (!cancelSchedule) run();
    return () => {
      cancelled = true;
      cancelSchedule?.();
    };
  }, [apiFetch, retry]);
  const recap = useMemo(
    () => summarizeSessions(sessions, Date.now(), weekStartDay),
    [sessions, weekStartDay],
  );
  return { ...recap, error };
}
